import React, { FC, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { Element } from '@components/frame';
import { font } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { WithColor } from '@styles/colors';

interface ErrorBoundaryDemoProps extends WithColor{
  scale: ScaleFunction;
}

const ErrorBoundaryDemo: FC<ErrorBoundaryDemoProps> = ({ C, scale }) => {
  const [shouldCrash, setShouldCrash] = useState(false);

  if (shouldCrash) {
    throw new Error('Demo: Intentional test error triggered from DevInfo → Error Boundary tab');
  }

  return (
    <Element
      {...{
        config: {
          width: '100%',
          paddingLeft: 10,
          paddingRight: 10,
          position: {
            top: 162,
            bottom: 0,
          },
          flexDirection: 'column',
        },
      }}
    >
      <ScrollView
        style={{ width: '100%' }}
        contentContainerStyle={{ paddingBottom: scale(60) }}
        showsVerticalScrollIndicator={false}
      >
        {/* Info Section */}
        <View
          style={{
            backgroundColor: C('Background/Light/Field lighter'),
            borderRadius: scale(10),
            padding: scale(16),
            marginBottom: scale(20),
          }}
        >
          <Text style={[font.Headline, { color: C('Label/Light/Primary'), marginBottom: scale(8) }]}>
            Error Boundaries
          </Text>
          <Text style={[font.Body, { color: C('Label/Light/Secondary'), marginBottom: scale(4) }]}>
            Error Boundaries catch unexpected screen crashes and show a recovery UI instead of a blank screen.
          </Text>
          <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>
            Every screen in the app is protected. Errors are automatically logged to Datadog.
          </Text>
        </View>

        {/* How It Works */}
        <View
          style={{
            backgroundColor: C('Background/Light/Field lighter'),
            borderRadius: scale(10),
            padding: scale(16),
            marginBottom: scale(20),
          }}
        >
          <Text style={[font.Headline, { color: C('Label/Light/Primary'), marginBottom: scale(8) }]}>How It Works</Text>
          {[
            '1. Tap the button below to simulate a screen crash',
            '2. The Error Boundary catches the crash — full-screen fallback appears',
            '3. The error is logged to Datadog with screen name and stack trace',
            '4. Tap "Try Again" to recover, or "Go Back" to navigate away',
            '5. "Report to Smart Factory" opens the bug report form',
          ].map((step) => (
            <Text key={step} style={[font.Footnote, { color: C('Label/Light/Secondary'), marginBottom: scale(4) }]}>
              {step}
            </Text>
          ))}
        </View>

        {/* Trigger Button */}
        <View style={{ alignItems: 'center' }}>
          <TouchableElement onPress={() => setShouldCrash(true)}>
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  boxStyle,
                  {
                    paddingVertical: scale(14),
                    paddingHorizontal: scale(32),
                    borderRadius: scale(10),
                    backgroundColor: C(pressHoverColor('System/Light/Red')),
                  },
                ]}
              >
                <Text
                  style={[
                    font['Button 1'],
                    {
                      color: '#FFFFFF',
                      textAlign: 'center',
                    },
                  ]}
                >
                  Trigger Test Error
                </Text>
              </View>
            )}
          </TouchableElement>
        </View>
      </ScrollView>
    </Element>
  );
};

export default ErrorBoundaryDemo;
